// uses ./appActions.less
import PropTypes from 'prop-types';

import React from 'react';
import { connect } from 'react-redux';
import APP_TYPE from '@integrations/pint-appstore-commons/src/enums/APP_TYPE.json';
import DISABLE_REASONS from '@integrations/pint-appstore-commons/src/enums/DISABLE_REASONS.json';
import UserAppActions from './UserAppActions';
import AdminAppActions from './AdminAppActions';
import { ViewDetailsButton, ButtonWrapper } from './appActionsItems';
import { openUrl } from '../appUrlActionCreators';
import APPACTIONS_DISPLAY_VARIANT from '../../../enums/APPACTIONS_DISPLAY_VARIANT';
import getTextForAppDisableReason from '../getTextForAppDisableReason';
import { RequiresConnect, RequiresProtect } from '../platform/PlatformTools';

const UPDATE_PLAN_URL = '/SimpleUI/acctSettings.do#planDetails';

function AppActions(props) {
    let content;
    const isEgIntegration = props.type === APP_TYPE.EGNYTE_UI_INTEGRATION;
    const isWebIntegration = props.type === APP_TYPE.EXTERNAL_WEB_INTEGRATION;
    const isEgApp = props.type === APP_TYPE.EGNYTE_APPS;
    const isProtectConfigurable = !!props.isProtectConfigurable;
    const hasStatus = !!props.status;
    const isOnTile = props.variant === APPACTIONS_DISPLAY_VARIANT.APPS_LIST_ITEM;
    const { domainSettings } = props;

    // TODO: this bit needs serious refactoring
    if (isOnTile) {
        if (props.isDisabled) {
            content = <RequiresConnect fallback={ViewDetailsButton}>{getButtonForDisabledApp(props)}</RequiresConnect>; // not sure if this should be done
        } else if (isProtectConfigurable && hasStatus) {
            content = (
                <RequiresProtect fallback={ViewDetailsButton}>
                    {getUserActions(props.isAdmin && props.actsAsAdmin, props)}
                </RequiresProtect>
            );
        } else if (isEgIntegration && hasStatus) {
            content = (
                <RequiresConnect fallback={ViewDetailsButton}>
                    {getUserActions(props.isAdmin && props.actsAsAdmin, props)}
                </RequiresConnect>
            );
        } else if (isWebIntegration && domainSettings) {
            content = getUserActions(props.isAdmin && props.actsAsAdmin, props);
        } else if (!isEgIntegration && isEgApp) {
            content = (
                <RequiresConnect fallback={ViewDetailsButton}>
                    {getButtonWrapper(isEgApp, isOnTile, props)}
                </RequiresConnect>
            );
        } else {
            content = <ViewDetailsButton {...props} />;
        }
    } else if (props.isDisabled) {
        content = <RequiresConnect>{getButtonForDisabledApp(props)}</RequiresConnect>; // not sure if this should be done
    } else if (isProtectConfigurable && hasStatus) {
        content = <RequiresProtect>{getUserActions(props.isAdmin && props.actsAsAdmin, props)}</RequiresProtect>;
    } else if (isEgIntegration && hasStatus) {
        content = <RequiresConnect>{getUserActions(props.isAdmin && props.actsAsAdmin, props)}</RequiresConnect>;
    } else if (isWebIntegration && domainSettings) {
        content = getUserActions(props.isAdmin && props.actsAsAdmin, props);
    } else if (props.platform === 'protect') {
        content = <RequiresProtect>{getButtonWrapper(isEgApp, isOnTile, props)}</RequiresProtect>;
    } else if (!isEgIntegration) {
        content = <RequiresConnect>{getButtonWrapper(isEgApp, isOnTile, props)}</RequiresConnect>;
    } else {
        content = undefined;
    }

    const className = [
        props.className,
        'ast-app-actions',
        props.variant === APPACTIONS_DISPLAY_VARIANT.APP_DETAILS ? 'ast-app-actions--on-details' : '',
    ].join(' ');

    return <div className={className}>{content}</div>;
}

function getButtonForDisabledApp(appActionProps) {
    if (
        appActionProps.disableReason.length === 1 &&
        appActionProps.disableReason.includes(DISABLE_REASONS.UPDATE_PLAN) &&
        appActionProps.isAdmin &&
        appActionProps.pageDomain
    ) {
        const urlActionFactory = (url) => () => {
            const absoluteUrl = `https://${appActionProps.pageDomain}${url}`;
            window.open(absoluteUrl, '_blank');
        };

        return (
            <ButtonWrapper
                isAdmin={appActionProps.isAdmin}
                actsAsAdmin={appActionProps.actsAsAdmin}
                isAvailable={appActionProps.isAvailable}
                label={getTextForAppDisableReason(appActionProps.disableReason, appActionProps.isAdmin)}
                getUrlAction={urlActionFactory}
                url={UPDATE_PLAN_URL}
                domainSettings={appActionProps.domainSettings}
            />
        );
    }
}

function getButtonWrapper(isEgApp, isOnTile, appActionProps) {
    const getUrlAction = (url) => () => {
        if (appActionProps.urlIsTrusted) {
            window.open(url, '_blank');
        } else {
            appActionProps.dispatchOpenUrl(appActionProps.appId, appActionProps.name, url);
        }
    };

    return (
        <ButtonWrapper
            className={isEgApp && isOnTile ? 'on-tile--center-options' : ''}
            isAdmin={appActionProps.isAdmin}
            actsAsAdmin={appActionProps.actsAsAdmin}
            isAvailable={appActionProps.isAvailable}
            label={appActionProps.urlGroupName}
            getUrlAction={getUrlAction}
            url={appActionProps.url}
            dropdownData={appActionProps.dropdownData}
            domainSettings={appActionProps.domainSettings}
        />
    );
}

function getUserActions(isAdmin, props) {
    return isAdmin ? (
        <AdminAppActions isAdmin={isAdmin} isAvailable={props.isAvailable} {...props} />
    ) : (
        <UserAppActions {...props} />
    );
}

AppActions.propTypes = {
    isAdmin: PropTypes.bool.isRequired,
    actsAsAdmin: PropTypes.bool.isRequired,
    isAvailable: PropTypes.bool,
    appId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    platform: PropTypes.string,
    status: PropTypes.string,
    appGroups: PropTypes.array, // only needed for UserAppActions
    urlGroupName: PropTypes.string,
    url: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.array.isRequired]),
    urlIsTrusted: PropTypes.bool,
    className: PropTypes.string.isRequired,
    variant: PropTypes.string.isRequired,
    dispatchOpenUrl: PropTypes.func.isRequired,
    dropdownData: PropTypes.shape({
        isOpen: PropTypes.bool.isRequired,
        onClick: PropTypes.func.isRequired,
    }),
    isDisabled: PropTypes.bool,
    disableReason: PropTypes.arrayOf(PropTypes.string),
    pageDomain: PropTypes.string,
    domainSettings: PropTypes.string,
};

const mapDispatchToProps = {
    dispatchOpenUrl: openUrl,
};

export default connect((state) => state.user, mapDispatchToProps)(AppActions);
